import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './Modal.module.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  labelledBy?: string;
  fullScreenOnMobile?: boolean;
  /** Wider desktop max-width for content-dense views; mobile unchanged. */
  wide?: boolean;
  /**
   * Cannot be dismissed by the user: no Escape, overlay click, close button,
   * or swipe-to-close. The caller's own controls are the only way out
   * (e.g. the login attestation, PLA-1110).
   */
  blocking?: boolean;
  children?: ReactNode;
}

const SWIPE_PX_FLOOR = 120;
const SWIPE_HEIGHT_FRACTION = 0.25;

export function Modal({ open, onClose, title, subtitle, footer, labelledBy, fullScreenOnMobile, wide, blocking, children }: ModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open || blocking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, blocking]);

  useEffect(() => {
    if (!open) {
      dragStartY.current = null;
      setDragY(0);
      setIsDragging(false);
    }
  }, [open]);

  // Lock background scroll while the modal is open so the page behind it stays
  // put. Compensate for the removed scrollbar width to avoid a layout shift,
  // and restore the prior values on close/unmount.
  useEffect(() => {
    if (!open) return;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open) return null;

  const onHandlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!fullScreenOnMobile || blocking) return;
    dragStartY.current = e.clientY;
    setIsDragging(true);
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const onHandlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current == null) return;
    const delta = e.clientY - dragStartY.current;
    setDragY(delta > 0 ? delta : 0);
  };

  const onHandlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current == null) return;
    const delta = e.clientY - dragStartY.current;
    const sheetHeight = modalRef.current?.offsetHeight ?? 0;
    const threshold = Math.min(SWIPE_PX_FLOOR, sheetHeight * SWIPE_HEIGHT_FRACTION);
    dragStartY.current = null;
    setIsDragging(false);
    if (delta > threshold) {
      onClose();
    } else {
      setDragY(0);
    }
  };

  const modalClasses = [
    styles.modal,
    fullScreenOnMobile ? styles.modalFullScreen : '',
    wide ? styles.modalWide : '',
    isDragging ? styles.dragging : '',
  ]
    .filter(Boolean)
    .join(' ');

  const modalStyle = dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined;

  // Portal to <body>: the overlay is position: fixed, and any ancestor with a
  // transform, filter or backdrop-filter (e.g. the frosted-glass Header) would
  // otherwise become its containing block and trap the sheet inside that box.
  return createPortal(
    <div
      className={styles.overlay}
      onClick={blocking ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        ref={modalRef}
        className={modalClasses}
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {fullScreenOnMobile && !blocking && (
          <div
            className={styles.grabHandleWrap}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
            role="button"
            tabIndex={-1}
            aria-label="Drag down to close"
          >
            <span className={styles.grabHandle} aria-hidden="true" />
          </div>
        )}
        {(title || subtitle) && (
          <header className={styles.header}>
            <div className={styles.titleRow}>
              {title}
              {!blocking && (
                <button type="button" className={styles.close} onClick={onClose} aria-label="Close">x</button>
              )}
            </div>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </header>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
